import React, { useState, useEffect, useContext, useReducer } from 'react'

import {
    Box,
    Dialog,
    DialogActions,
    DialogContent,
    DialogTitle,
    Tooltip,
    Button,
    TextField,
    Divider,
    Select,
    MenuItem,
    InputLabel,
    FormControl,
    FormControlLabel,
    Checkbox,
    List,
    ListItem,
    IconButton,
    Switch,
    Alert
} from '@mui/material'

import CloseIcon from '@mui/icons-material/Close';
import RemoveIcon from '@mui/icons-material/Remove';
import AddIcon from '@mui/icons-material/Add';
import HelpIcon from '@mui/icons-material/Help';

import api from '../../../api';

import useNotification from '../../Notification';
import defaultReducer from '../../../utils/defaultReducer';
import IntegerOnly from '../../../utils/IntegerOnly';
import MultipleSelections from '../../Input/MultipleSelections';
import VariableTextField from '../../Input/VariableTextField';
import { delayedChange } from '../../ManageCalls/FilterComponent';
import ContactCondition, {
    buildContactConditionPayload,
    getContactConditionErrors,
    normalizeContactConditions
} from './ContactCondition';
import EscalationActions, {
    buildEscalationActionPayload,
    getEscalationActionErrors,
    normalizeEscalationActions
} from './EscalationActions';

import { getSession } from '../../../auth';

import './styles.css'

const action = (data) => {
    if (data?.id)
        return 'Editar'
    return 'Adicionar'
}

const treatnumber = (number) => {
    if (number.length > 0 && number[0] === '+')
        return number
    return `+${number}`
}

const getResponseList = (response) => {
    if (!response || response.status !== 200)
        return []
    if (Array.isArray(response.data))
        return response.data
    return response.data?.results || []
}

const EditEscalation = ({ data, allQueues, rows, setParentState, method, allChatBots, forcedType }) => {

    const [state, setState] = useReducer(defaultReducer, {
        description      : '',
        chatBotsChanged  : false,
        queuesChanged    : false,
        chatBotsChecked  : {},
        queuesChecked    : {},
        sendQueueMessage : false,
        messagesChanged  : false,
        chatBotMessages: [{
            timeout : null,
            message : null
        }],
        queueMessages: [{
            timeout : null,
            message : null
        }],
        sendOnChatMessage: false,
        onChatMessages: [{
            timeout : null,
            message : null
        }],
        escalate          : true,
        escalateTime      : null,
        escalateQueue     : 0,
        escalateMessage   : null,
        agentInactivity   : true,
        customerInactivity: false,
        queueKey          : 0,
        onChatKey         : 0,
        escalateKey       : 0,
        chatbotEscalation : (!forcedType || forcedType === "chatbots"),
        contactConditions : [],
        customFields      : [],
        contactGroups     : [],
        contactEnterprises: [],
        users             : [],
        labels            : [],
        journeys          : [],
        actions           : [],
        actionsChanged    : false
    })

    const checkButtonText = () => {
        const text = []

        if (! state.description)
            text.push(<Box>Digite a descrição da automação.</Box>)

        if (state.chatbotEscalation && Object.keys(state.chatBotsChecked).filter(key => state.chatBotsChecked[key].checked).length === 0)
            text.push(<Box>Escolha pelo menos um chatbot.</Box>)

        if (! state.chatbotEscalation && Object.keys(state.queuesChecked).filter(key => state.queuesChecked[key].checked).length === 0)
            text.push(<Box>Escolha pelo menos uma fila de atendimento.</Box>)

        if (! state.escalateTime && state.escalateQueue)
            text.push(<Box>Defina o tempo para transbordo.</Box>)

        if (state.escalateTime && ! state.escalateQueue)
            text.push(<Box>Escolha uma fila para transbordo.</Box>)

        getContactConditionErrors(state.contactConditions).forEach(error => {
            text.push(<Box>{error}</Box>)
        })

        getEscalationActionErrors(state.actions).forEach(error => {
            text.push(<Box>{error}</Box>)
        })

        if (text.length == 0)
            return ''

        return (text)
    }

    const updateContactGroups = (groups) => setState({ contactGroups: groups })
    const updateContactEnterprises = (enterprises) => setState({ contactEnterprises: enterprises })
    const updateUsers = (users) => setState({ users: users })
    const updateLabels = (labels) => setState({ labels: labels })
    const setListLoading = () => {}

    const searchContactGroups = (value) => delayedChange(value, 'group', updateContactGroups, setListLoading)
    const searchContactEnterprises = (value) => delayedChange(value, 'enterprise', updateContactEnterprises, setListLoading)
    const searchUsers = (value) => delayedChange(value, 'user', updateUsers, setListLoading)
    const searchLabels = (value) => delayedChange(value, 'label', updateLabels, setListLoading)

    const changeSendChatBotMessage = () => {
        setState({
            messagesChanged    : true,
            sendChatBotMessage : ! state.sendChatBotMessage
        })
    }

    const changeSendQueueMessage = () => {
        setState({
            messagesChanged  : true,
            sendQueueMessage : ! state.sendQueueMessage
        })
    }

    const changeSendOnChatMessage = () => {
        setState({
            messagesChanged   : true,
            sendOnChatMessage : ! state.sendOnChatMessage
        })
    }

    const changeEscalate = () => {
        setState({
            escalate        : ! state.escalate,
            escalateTime    : null,
            escalateQueue   : 0,
            escalateMessage : null,
        })
    }

    const getSelectedChatBots = () => {
        if (allChatBots) {
            const chatBots = Object.keys(state.chatBotsChecked).filter((chatBot) => {
                return state.chatBotsChecked[chatBot].checked;
            });
            return chatBots;
        }
        return [];
    }

    const getSelectedData = () => {
        if (allQueues) {
            const queues = Object.keys(state.queuesChecked).filter((queue) => {
                return state.queuesChecked[queue].checked;
            });
            return queues;
        }
        return [];
    }

    const getPayload = () => {
        const payload = { "description": state.description }
        payload["contact_condition"] = buildContactConditionPayload(state.contactConditions)

        if (state.actionsChanged || method === 'POST') {
            payload["actions_changed"] = true
            payload["actions"] = buildEscalationActionPayload(state.actions)
        }

        if (state.chatbotEscalation) {
            // Adding chatbot settings
            if (state.chatBotsChanged) {
                payload["chatbots_changed"] = state.chatBotsChanged
                payload["chatbots"] = Object.keys(state.chatBotsChecked).filter(key => state.chatBotsChecked[key].checked)
            }

            // Adding escalate chatbot messages
            if (state.messagesChanged) {
                payload["messages_changed"] = state.messagesChanged
                if (state.sendChatBotMessage) {
                    if (! ('messages' in payload))
                        payload['messages'] = []
                    state.chatBotMessages.forEach((message) => {
                        if (message.timeout && message.message) {
                            payload['messages'].push({
                                timeout : message.timeout * 60,
                                message : message.message,
                                status  : 'CHATBOT'
                            })
                        }
                    })
                }
            }
        }

        if (! state.chatbotEscalation) {
            // Adding queue settings
            if (state.queuesChanged) {
                payload["queues_changed"] = state.queuesChanged
                payload["queues"] = Object.keys(state.queuesChecked).filter(key => state.queuesChecked[key].checked)
            }

            // Adding escalate queue messages
            if (state.messagesChanged) {
                payload["messages_changed"] = state.messagesChanged
                if (state.sendQueueMessage) {
                    if (! ('messages' in payload))
                        payload['messages'] = []
                    state.queueMessages.forEach((message) => {
                        if (message.timeout && message.message) {
                            payload['messages'].push({
                                timeout : message.timeout * 60,
                                message : message.message,
                                status  : 'QUEUE'
                            })
                        }
                    })
                }
            }

            // Adding escalate onchat messages
            if (state.messagesChanged) {
                payload["messages_changed"] = state.messagesChanged
                if (state.sendOnChatMessage) {
                    if (! ('messages' in payload))
                        payload['messages'] = []
                    state.onChatMessages.forEach((message) => {
                        if (message.timeout && message.message) {
                            payload['messages'].push({
                                timeout : message.timeout * 60,
                                message : message.message,
                                status  : 'ONCHAT'
                            })
                        }
                    })
                }
            }
        }

        // Adding escalate settings
        payload["timeout"] = state.escalateTime * 60
        payload["destination_queue"] = state.escalateQueue
        payload["message"] = state.escalateMessage
        payload["agent_inactivity"] = state.agentInactivity
        payload["customer_inactivity"] = state.customerInactivity

        if (payload["destination_queue"] === 0)
            payload["destination_queue"] = null

        let isValid = true
        let environMinTime = 1*60
        const environment = window.location.href
        if (environment.includes("kwik.app.br"))
            environMinTime = 5*60

        if (payload["timeout"] != 0 && payload["timeout"] < (environMinTime)) {
            isValid = false
            useNotification(
                'Ops!',
                'O tempo mínimo para configuração é de 5 minutos.',
                'warning'
            )
            return
        }

        if ('messages' in payload) {
            payload['messages'].forEach((each) => {
                if (each['timeout'] < (environMinTime)) {
                    isValid = false
                    useNotification(
                        'Ops!',
                        'O tempo mínimo para configuração é de 5 minutos.',
                        'warning'
                    )
                    return
                }
            })
        }

        if (! isValid)
            return

        return payload
    }

    const submit = async () => {
        const enterpriseId = getSession().profile.enterprise.id
        const payload = getPayload()

        if (! payload)
            return

        let response

        if (method === 'POST')
            response = await api.post(`api/enterprises/${enterpriseId}/escalation/`, payload)
        else
            response = await api.patch(`api/enterprises/${enterpriseId}/escalation/${data.id}/`, payload)

        if (response.status !== 201 && response.status !== 200) {
            if (response.status === 400) {
                if ("chatbots" in response.data) {
                    const message = Array.isArray(response.data.chatbots) && response.data.chatbots.length
                        ? response.data.chatbots[0]
                        : 'Revise os chatbots selecionados.'
                    useNotification(
                        'Ops!',
                        message,
                        'warning'
                    )
                    return
                }
                else if ("queues" in response.data) {
                    const message = Array.isArray(response.data.queues) && response.data.queues.length
                        ? response.data.queues[0]
                        : 'Revise as filas selecionadas.'
                    useNotification(
                        'Ops!',
                        message,
                        'warning'
                    )
                    return
                }
                else if ("actions" in response.data || "contact_condition" in response.data) {
                    const fieldErrors = response.data.actions || response.data.contact_condition
                    const message = Array.isArray(fieldErrors) && fieldErrors.length
                        ? fieldErrors[0]
                        : 'Revise a condição do contato ou as ações adicionais.'
                    useNotification(
                        'Ops!',
                        message,
                        'warning'
                    )
                    return
                }
            }
            useNotification(
                'Ops!',
                'Ocorreu um erro na requisição.',
                'danger'
            )
            return
        }

        if (method === 'POST') {
            useNotification(
                'Sucesso!',
                'Sua automação foi criada com sucesso.',
                'success'
            )

            setParentState({
                rows: [...rows, response.data],
                editEscalation: null
            })
            return
        }

        useNotification(
            'Sucesso!',
            'Sua automação foi atualizada com sucesso.',
            'success'
        )

        const updatedRows = [ ...rows ]
        updatedRows.forEach((value, key) => {
            if (value.id == response.data['id'])
                updatedRows[key] = response.data
        })

        setParentState({
            rows: updatedRows,
            editEscalation: null
        })
    }

    useEffect(async () => {
        const enterpriseId = getSession().profile.enterprise.id
        try {
            const [
                customFieldsResponse,
                groupsResponse,
                labelsResponse,
                usersResponse,
                enterprisesResponse,
                journeysResponse,
            ] = await Promise.all([
                api.get(`api/enterprises/${enterpriseId}/custom_fields/`),
                api.get(`api/enterprises/${enterpriseId}/contact_group/noserialize/?page_size=100`),
                api.get(`api/enterprises/${enterpriseId}/labelchat/`),
                api.get(`api/enterprises/${enterpriseId}/users/get_users_emails/`),
                api.get(`api/enterprises/${enterpriseId}/contact_enterprises/simplified/?limit=50&offset=0`),
                api.get(`api/enterprises/${enterpriseId}/journeys/`),
            ])

            setState({
                customFields       : getResponseList(customFieldsResponse),
                contactGroups      : getResponseList(groupsResponse),
                labels             : getResponseList(labelsResponse),
                users              : getResponseList(usersResponse),
                contactEnterprises : getResponseList(enterprisesResponse),
                journeys           : getResponseList(journeysResponse),
            })
        } catch (error) {}
    }, [])

    useEffect(async () => {
        if (Object.keys(data).length === 0)
            setState({
                queuesChecked   : allQueues,
                chatBotsChecked : allChatBots,
            })
    }, [allQueues, allChatBots])

    useEffect(async () => {
        if (! data.id)
            return

        const queuesChecked = { ...allQueues }
        Object.keys(queuesChecked).forEach((key) => {
            queuesChecked[key].checked = false
            data.queues.forEach((queue) => {
                if (key === queue.queue)
                    queuesChecked[key].checked = true
            })
        })

        const chatBotsChecked = { ...allChatBots }
        Object.keys(chatBotsChecked).forEach((key) => {
            chatBotsChecked[key].checked = false
            data.chatbots.forEach((chatbot) => {
                if (key === chatbot.chatbot_id)
                    chatBotsChecked[key].checked = true
            })
        })

        const chatBotMessages = []
        const queueMessages   = []
        const onChatMessages  = []
        if (data.messages.length !== 0) {
            let row
            data.messages.forEach((each) => {
                row = {...each}
                row['timeout'] = Math.trunc(row['timeout'] / 60)
                switch (each.status) {
                    case 'CHATBOT':
                        chatBotMessages.push(row)
                        break
                    case 'QUEUE':
                        queueMessages.push(row)
                        break
                    case 'ONCHAT':
                        onChatMessages.push(row)
                        break
                    default:
                        break
                }
            })
        }

        let hasChatBotMessages = (chatBotMessages.length !== 0)
        let hasQueueMessages   = (queueMessages.length !== 0)
        let hasOnChatMessages  = (onChatMessages.length !== 0)

        if (chatBotMessages.length === 0)
            chatBotMessages.push({ timeout: null, message: null })
        if (queueMessages.length === 0)
            queueMessages.push({ timeout: null, message: null })
        if (onChatMessages.length === 0)
            onChatMessages.push({ timeout: null, message: null })

        let escalate = false
        if (data.timeout && data.destination_queue)
            escalate = true

        const chatbotEscalation = data.chatbots.length !== 0

        setState({
            description        : data.description,
            chatBotsChecked    : chatBotsChecked,
            queuesChecked      : queuesChecked,
            sendChatBotMessage : hasChatBotMessages,
            chatBotMessages    : chatBotMessages,
            sendQueueMessage   : hasQueueMessages,
            queueMessages      : queueMessages,
            sendOnChatMessage  : hasOnChatMessages,
            onChatMessages     : onChatMessages,
            escalate           : escalate,
            escalateTime       : Math.trunc(data.timeout / 60),
            escalateQueue      : data.destination_queue,
            escalateMessage    : data.message,
            agentInactivity    : data.agent_inactivity ?? true,
            customerInactivity : data.customer_inactivity ?? false,
            renderKey          : `multiple-selections-${Math.random()}`,
            chatbotEscalation  : chatbotEscalation,
            contactConditions  : normalizeContactConditions(data.contact_condition),
            actions            : normalizeEscalationActions(data.actions || [], chatbotEscalation),
            actionsChanged     : false
        })
    }, [data])

    const updateChatBotMessage = (id, value, type, variable=false) => {
        const messages = state.chatBotMessages
        messages.forEach((each, index) => {
            if (index === id) {
                if (variable)
                    each[type] = each[type]+value
                else
                    each[type] = value
            }
        })
        setState({
            messagesChanged : true,
            chatBotMessages   : messages
        })
    }

    const updateQueueMessage = (id, value, type, variable=false) => {
        const messages = state.queueMessages
        messages.forEach((each, index) => {
            if (index === id) {
                if (variable)
                    each[type] = each[type]+value
                else
                    each[type] = value
            }
        })
        setState({
            messagesChanged : true,
            queueMessages   : messages
        })
    }

    const removeQueueMessage = (id) => {
        if (state.queueMessages.length === 1)
            return
        setState({
            messagesChanged : true,
            queueMessages   : state.queueMessages.filter((message, index) => index !== id)
        })
    }

    const removeChatBotMessage = (id) => {
        if (state.chatBotMessages.length === 1)
            return
        setState({
            messagesChanged : true,
            chatBotMessages : state.chatBotMessages.filter((message, index) => index !== id)
        })
    }

    const addChatBotMessageRow = () => {
        setState({ chatBotMessages: [...state.chatBotMessages, {
            timeout : null,
            message : null
        }]})
    }

    const addQueueMessageRow = () => {
        setState({ queueMessages: [...state.queueMessages, {
            timeout : null,
            message : null
        }]})
    }

    const updateOnChatMessage = (id, value, type, variable=false) => {
        const messages = state.onChatMessages
        messages.forEach((each, index) => {
            if (index === id)
                if (variable)
                    each[type] = each[type]+value
                else
                    each[type] = value
        })
        setState({
            messagesChanged : true,
            onChatMessages  : messages
        })
    }

    const removeOnChatMessage = (id) => {
        if (state.onChatMessages.length === 1)
            return
        setState({
            messagesChanged : true,
            onChatMessages  : state.onChatMessages.filter((message, index) => index !== id)
        })
    }

    const addOnChatMessageRow = () => {
        setState({ onChatMessages: [...state.onChatMessages, {
            timeout : null,
            message : null
        }]})
    }

    return (
        <>
            <Dialog open={true}
                fullWidth
                // disablePortal
                // disableEnforceFocus
                // sx={{ position: "absolute" }}
                maxWidth={"sm"}
            >
                <DialogTitle id="customized-dialog-title" className={'group-flexbox'}>
                    {action(data)} automação
                    <Button onClick={() => window.open("https://instant.getoutline.com/s/canal-do-cliente/doc/configurando-jornada-de-atendimento-RzL8hS97Nb", "_blank")}>FAQ</Button>
                </DialogTitle>
                <Divider />
                <DialogContent className='piloto-modal-flex'>
                    <Box className="category-title">
                        Descrição
                    </Box>
                    <TextField
                        fullWidth
                        size="small"
                        label="Descrição"
                        type="text"
                        variant="outlined"
                        value={state.description}
                        onChange={(event) => setState({ description: event.target.value })}
                    />
                    {action(data) === 'Adicionar' && ! forcedType &&
                        <Box className='BuilderBotSelectorBox'>
                            <Button
                                variant={state.chatbotEscalation ? 'contained' : ''}
                                onClick={() => setState({ chatbotEscalation: true })}
                            >
                                ChatBots
                            </Button>
                            <Button
                                variant={!state.chatbotEscalation ? 'contained' : ''}
                                onClick={() => setState({ chatbotEscalation: false })}
                            >
                                Filas
                            </Button>
                        </Box>
                    }
                    {action(data) !== 'Adicionar' &&
                        <Box className="category-title">
                            {state.chatbotEscalation ? 'Chatbots' : 'Filas'}
                        </Box>
                    }
                    {state.chatbotEscalation &&
                        <>
                            <Box>
                                <MultipleSelections
                                    title="Chatbots"
                                    selectedData={getSelectedChatBots()}
                                    dataIsObject={true}
                                    objectDataHasTitle="name"
                                    objectDataHasChecked="checked"
                                    dataProp={{
                                        __select_all__: { name: "Selecionar todos" },
                                        ...state.chatBotsChecked
                                    }}
                                    getSelect={(chatBotsChecked) => setState({
                                        chatBotsChanged : true,
                                        chatBotsChecked : chatBotsChecked
                                    })}
                                    key={state.renderKey}
                                />
                            </Box>
                            <ContactCondition
                                conditions={state.contactConditions}
                                onChange={(conditions) => setState({ contactConditions: conditions })}
                                customFields={state.customFields}
                                groups={state.contactGroups}
                                enterprises={state.contactEnterprises}
                                users={state.users}
                                onGroupInputChange={searchContactGroups}
                                onEnterpriseInputChange={searchContactEnterprises}
                                onUserInputChange={searchUsers}
                            />
                            <Box display="flex" flexDirection="column" gap={1} p={2} border="1px solid #c4c4c4" borderRadius="4px">
                                <Box className={state.sendChatBotMessage ? "category-title checkbox" : "category-title checkbox unchecked"}>
                                    <Switch
                                        checked={state.sendChatBotMessage}
                                        onChange={changeSendChatBotMessage}
                                        inputProps={{ 'aria-label': 'controlled' }}
                                    />
                                    Mensagens
                                </Box>
                                {state.sendChatBotMessage &&
                                    <>
                                        <Alert sx={{ marginBottom: 1 }} severity="info">
                                            Defina abaixo as mensagens que serão enviadas ao cliente por inatividade do cliente durante o fluxo do ChatBot.
                                        </Alert>
                                        {state.chatBotMessages.map((each, index) => (
                                            <Box className="escalation-time-row">
                                                <TextField
                                                    sx={{ width: "70px" }}
                                                    size="small"
                                                    label="Minutos"
                                                    type="text"
                                                    variant="outlined"
                                                    value={each.timeout}
                                                    onChange={(event) => updateChatBotMessage(index, IntegerOnly(event.target.value, 0, 86400), 'timeout')}
                                                />
                                                <VariableTextField
                                                    sx={{ flex: 1 }}
                                                    multiline={true}
                                                    size="small"
                                                    label="Mensagem"
                                                    type="text"
                                                    variant="outlined"
                                                    value={each.message}
                                                    onChange={(event) => updateChatBotMessage(index, event.target.value, 'message')}
                                                    onVariableSelect={(value) => {
                                                        updateChatBotMessage(index, value, 'message', true)
                                                        setState({ chatBotKey: `onchat-text-field-${Math.random()}` })
                                                    }}
                                                    key={`${state.chatBotKey}-${index}`}
                                                />
                                                <Button
                                                    className="remove-add-icon"
                                                    variant="contained"
                                                    color="error"
                                                    onClick={() => removeChatBotMessage(index)}
                                                >
                                                    <RemoveIcon />
                                                </Button>
                                                <Button
                                                    className="remove-add-icon"
                                                    variant="contained"
                                                    onClick={addChatBotMessageRow}
                                                >
                                                    <AddIcon />
                                                </Button>
                                            </Box>
                                        ))}
                                    </>
                                }
                            </Box>
                        </>
                    }
                    {! state.chatbotEscalation &&
                        <>
                            <Box display="flex" gap={2}>
                                <FormControlLabel
                                    control={
                                        <Checkbox
                                            checked={state.agentInactivity}
                                            onChange={() => setState({ agentInactivity: !state.agentInactivity })}
                                        />
                                    }
                                    label="Inatividade do agente"
                                />
                                <FormControlLabel
                                    control={
                                        <Checkbox
                                            checked={state.customerInactivity}
                                            onChange={() => setState({ customerInactivity: !state.customerInactivity })}
                                        />
                                    }
                                    label="Inatividade do cliente"
                                />
                            </Box>
                            <Box>
                                <MultipleSelections
                                    title="Filas"
                                    selectedData={getSelectedData()}
                                    dataIsObject={true}
                                    objectDataHasTitle="name"
                                    objectDataHasChecked="checked"
                                    dataProp={{
                                        __select_all__: { name: "Selecionar todas" },
                                        ...state.queuesChecked
                                    }}
                                    getSelect={(queuesChecked) => setState({
                                        queuesChanged : true,
                                        queuesChecked : queuesChecked
                                    })}
                                    key={state.renderKey}
                                />
                            </Box>
                            <ContactCondition
                                conditions={state.contactConditions}
                                onChange={(conditions) => setState({ contactConditions: conditions })}
                                customFields={state.customFields}
                                groups={state.contactGroups}
                                enterprises={state.contactEnterprises}
                                users={state.users}
                                onGroupInputChange={searchContactGroups}
                                onEnterpriseInputChange={searchContactEnterprises}
                                onUserInputChange={searchUsers}
                            />
                            <Box display="flex" flexDirection="column" gap={1} p={2} border="1px solid #c4c4c4" borderRadius="4px">
                                <Box className={state.sendQueueMessage ? "category-title checkbox" : "category-title checkbox unchecked"}>
                                    <Switch
                                        checked={state.sendQueueMessage}
                                        onChange={changeSendQueueMessage}
                                        inputProps={{ 'aria-label': 'controlled' }}
                                    />
                                    Mensagens (cliente em fila)
                                </Box>
                                {state.sendQueueMessage &&
                                    <>
                                        <Alert sx={{ marginBottom: 1 }} severity="info">
                                            Defina abaixo as mensagens que serão enviadas ao cliente aguardando atendimento em fila.
                                        </Alert>
                                        {state.queueMessages.map((each, index) => (
                                            <Box className="escalation-time-row">
                                                <TextField
                                                    sx={{ width: "70px" }}
                                                    size="small"
                                                    label="Minutos"
                                                    type="text"
                                                    variant="outlined"
                                                    value={each.timeout}
                                                    onChange={(event) => updateQueueMessage(index, IntegerOnly(event.target.value, 0, 86400), 'timeout')}
                                                />
                                                <VariableTextField
                                                    sx={{ flex: 1 }}
                                                    multiline={true}
                                                    size="small"
                                                    label="Mensagem"
                                                    type="text"
                                                    variant="outlined"
                                                    value={each.message}
                                                    onChange={(event) => updateQueueMessage(index, event.target.value, 'message')}
                                                    onVariableSelect={(value) => {
                                                        updateQueueMessage(index, value, 'message', true)
                                                        setState({ queueKey: `onchat-text-field-${Math.random()}` })
                                                    }}
                                                    key={`${state.queueKey}-${index}`}
                                                />
                                                <Button
                                                    className="remove-add-icon"
                                                    variant="contained"
                                                    color="error"
                                                    onClick={() => removeQueueMessage(index)}
                                                >
                                                    <RemoveIcon />
                                                </Button>
                                                <Button
                                                    className="remove-add-icon"
                                                    variant="contained"
                                                    onClick={addQueueMessageRow}
                                                >
                                                    <AddIcon />
                                                </Button>
                                            </Box>
                                        ))}
                                    </>
                                }
                            </Box>
                            <Box display="flex" flexDirection="column" gap={1} p={2} border="1px solid #c4c4c4" borderRadius="4px">
                                <Box className={state.sendOnChatMessage ? "category-title checkbox" : "category-title checkbox unchecked"}>
                                    <Switch
                                        checked={state.sendOnChatMessage}
                                        onChange={changeSendOnChatMessage}
                                        inputProps={{ 'aria-label': 'controlled' }}
                                    />
                                    Mensagens (cliente em atendimento)
                                </Box>
                                {state.sendOnChatMessage &&
                                    <>
                                        <Alert sx={{ marginBottom: 1 }} severity="info">
                                            Defina abaixo as mensagens que serão enviadas ao cliente por inatividade do cliente durante atendimento.
                                        </Alert>
                                        {state.onChatMessages.map((each, index) => (
                                            <Box className="escalation-time-row">
                                                <TextField
                                                    sx={{ width: "70px" }}
                                                    size="small"
                                                    label="Minutos"
                                                    type="text"
                                                    variant="outlined"
                                                    value={each.timeout}
                                                    onChange={(event) => updateOnChatMessage(index, IntegerOnly(event.target.value, 0, 86400), 'timeout')}
                                                />
                                                <VariableTextField
                                                    sx={{ flex: 1 }}
                                                    multiline={true}
                                                    size="small"
                                                    label="Mensagem"
                                                    type="text"
                                                    variant="outlined"
                                                    value={each.message}
                                                    onChange={(event) => updateOnChatMessage(index, event.target.value, 'message')}
                                                    onVariableSelect={(value) => {
                                                        updateOnChatMessage(index, value, 'message', true)
                                                        setState({ queueKey: `queue-text-field-${Math.random()}` })
                                                    }}
                                                    key={`${state.onChatKey}-${index}`}
                                                />
                                                <Button
                                                    className="remove-add-icon"
                                                    variant="contained"
                                                    color="error"
                                                    onClick={() => removeOnChatMessage(index)}
                                                >
                                                    <RemoveIcon />
                                                </Button>
                                                <Button
                                                    className="remove-add-icon"
                                                    variant="contained"
                                                    onClick={addOnChatMessageRow}
                                                >
                                                    <AddIcon />
                                                </Button>
                                            </Box>
                                        ))}
                                    </>
                                }
                            </Box>
                        </>
                    }
                    <Box display="flex" flexDirection="column" gap={1} p={2} border="1px solid #c4c4c4" borderRadius="4px">
                        <Box className={state.escalate ? "category-title checkbox" : "category-title checkbox unchecked"}>
                            <Switch
                                checked={state.escalate}
                                onChange={changeEscalate}
                                inputProps={{ 'aria-label': 'controlled' }}
                            />
                            Transbordo
                        </Box>
                        {state.escalate &&
                            <>
                                <Alert sx={{ marginBottom: 1 }} severity="info">
                                    Defina abaixo o tempo máximo de inatividade para que o atendimento seja transferido para outra fila de atendimento.
                                </Alert>
                                <Box className="escalation-time-row">
                                    <TextField
                                        sx={{ width: "70px" }}
                                        size="small"
                                        label="Minutos"
                                        type="text"
                                        variant="outlined"
                                        value={(state.escalateTime) ? state.escalateTime : null}
                                        onChange={(event) => setState({ escalateTime: IntegerOnly(event.target.value, 0, 86400) })}
                                    />
                                    <FormControl sx={{ flex: 1 }} size="small">
                                        <InputLabel id="escalate-queue-label">Fila de transbordo</InputLabel>
                                        <Select
                                            labelId="escalate-queue-label"
                                            id="escalate-queue"
                                            value={state.escalateQueue}
                                            label="Fila de transbordo"
                                            onChange={(event) => setState({ escalateQueue: event.target.value })}
                                        >
                                            <MenuItem value={0}>Selecione um fila...</MenuItem>
                                            {Object.keys(allQueues).map((key) => (
                                                <MenuItem value={key}>{allQueues[key]['name']}</MenuItem>
                                            ))}
                                        </Select>
                                    </FormControl>
                                </Box>
                                <Box>
                                    <VariableTextField
                                        fullWidth={true}
                                        multiline={true}
                                        size="small"
                                        label="Mensagem de transbordo"
                                        type="text"
                                        variant="outlined"
                                        value={state.escalateMessage}
                                        onChange={(event) => setState({ escalateMessage: event.target.value })}
                                        onVariableSelect={(value) => setState({
                                            escalateMessage: state.escalateMessage ? `${state.escalateMessage}${value}` : value,
                                            escalateKey: `escalate-text-field-${Math.random()}`
                                        })}
                                        key={state.escalateKey}
                                    />
                                </Box>
                            </>
                        }
                    </Box>
                    <EscalationActions
                        actions={state.actions}
                        onChange={(actions) => setState({
                            actions: actions,
                            actionsChanged: true,
                        })}
                        labels={state.labels}
                        groups={state.contactGroups}
                        journeys={state.journeys}
                        onGroupInputChange={searchContactGroups}
                        onLabelInputChange={searchLabels}
                    />
                </DialogContent>
                <DialogActions style={{ padding: "10px 20px 10px 10px" }}>
                    <Button onClick={() => setParentState({ editEscalation: null })}>
                        Cancelar
                    </Button>
                    <Tooltip title={checkButtonText()} placement="top" describeChild={true}>
                        <span>
                            <Button
                                disabled={checkButtonText()}
                                onClick={submit}>
                                Salvar
                            </Button>
                        </span>
                    </Tooltip>
                </DialogActions>
            </Dialog >
        </>
    )
}

export default EditEscalation
